import { takeLatest, put, select } from 'redux-saga/effects';
import { get } from 'lodash';
import update from 'immutability-helper';
import { message } from 'antd';
import {
  getFirstAppliedFilter,
  currentMetricsMetaSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import { checkIfRuleAlreadyExist, createFilterRule } from '@utils/filter';
import { pushDrilldownFilter, pushMultiDrilldownFilter } from '@store/dimensionContainer/actions';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { applyFilter } from '@store/common/actions';
import { isAppModule, isEiModule } from '@utils/constants';

import {
  SET_INITIAL_FILTERS_TO_DRAWER,
  SET_INITIAL_TRENDS_FILTER_TO_DRAWER,
} from '@store/noc/drawer/types';
import {
  setMetrics as setSummaryMetrics,
  selectedMetricChart,
} from '@store/metricsChartContainer/actions';
import {
  getMetricDimensionMapping,
  METRIC_DIMENSION_GLOBAL_SORT_BY_MAPPING,
  getMetricSpecificDimensionMapping,
} from '@src/utils/metrics/metricFilterDimensionMapping';
import { preserveOrder } from '@components/common/DnD/utils';
import { metricSelector } from '@store/metricsChartContainer/selectors';
import { buildErrorRule } from '@utils/filter/createErrorCodeFilter';
import { METRIC_FILTER_TYPES } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import { getSelectedChartId } from '@components/metrics-panel-hz/MetricsChartContainer/utils';
import { metricFilterErrorCodesSelector } from './selectors';
import drawerSaga from './drawerSaga';
import dimensionSaga from './dimensionSaga';
import { setDimensionMetrics, setGlobalOrderByMetric } from './actions';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { COMPARE_TYPES, ViewContexts } from './reducer';
import * as actions from './actions';
import * as types from './types';
import {
  combineWithMetricDeDuplication,
  getInvertedPercentageMetricByKey,
} from '@src/utils/metrics/metricHelpers';

export const COMPARE_LIMIT = 20;

function* init() {
  // placeholder
  yield null;
}

function* secondaryMetricUpdates(action) {
  const { metricFilter } = action;
  const metric = metricFilter.metric;
  const currentMetricsMeta = yield select(currentMetricsMetaSelector);
  const metricInfo = currentMetricsMeta[metric];
  const selectedMetricsFromStore = yield select(metricSelector);

  // AA should only support one metric filter
  if (isAppModule) {
    yield put(actions.clearMetricFilter());
  }

  // user can only slice on the selected metric
  // redudant with chart onSelect action (temp fix)
  const chartId = getSelectedChartId(metric);
  const metricName = getInvertedPercentageMetricByKey({ metricKey: metric, defaultValue: metric });
  yield put(selectedMetricChart(metricName, chartId));

  let metricFilterDimensionTableMetrics: any = [];
  const metricSpecificDimensions = getMetricSpecificDimensionMapping(metric);
  if (metricSpecificDimensions) {
    metricFilterDimensionTableMetrics = metricSpecificDimensions;
  } else {
    if (isEiModule) {
      const metricCategory = Array.isArray(metricInfo.category)
        ? metricInfo.category[0]
        : metricInfo.category;
      metricFilterDimensionTableMetrics = getMetricDimensionMapping(metricCategory);
    }
  }
  if (METRIC_DIMENSION_GLOBAL_SORT_BY_MAPPING[metric]) {
    const sortByMetric = METRIC_DIMENSION_GLOBAL_SORT_BY_MAPPING[metric];
    yield put(setGlobalOrderByMetric(sortByMetric));
  }
  if (metricFilterDimensionTableMetrics?.length) {
    // always show selected metric along with mapped metrics on dimension tables
    const mergedMetrics = combineWithMetricDeDuplication(metricFilterDimensionTableMetrics, [
      metricName,
    ]);

    yield put(setDimensionMetrics(mergedMetrics));
    yield put(
      setSummaryMetrics(
        preserveOrder(selectedMetricsFromStore, [
          ...selectedMetricsFromStore,
          ...metricFilterDimensionTableMetrics,
        ]),
      ),
    );
  }

  if (metricFilter) {
    yield put(actions.addMetricFilter(metricFilter));
  }
}

// apply drilldown filter
function* dimensionDrillown(action) {
  const { dimension, row } = action;

  // get current dimesion and filter
  const dimensionList = yield select(allDimensionsListSelector);
  const filter = yield select(getFirstAppliedFilter);
  const dimensionObj = dimensionList.find((d) => d.groupBy === dimension);

  // create drill down rule
  const drilldownRule = createFilterRule(
    dimensionObj,
    get(row, 'name[0]', ''),
    get(row, 'value[0]', ''),
  );
  // check if the drill down rule already exists in the applied filter
  const doesRuleExist = checkIfRuleAlreadyExist(filter.rule, drilldownRule);

  // add drilldown rule to current filter rules if it doesn't exist already
  if (!doesRuleExist) {
    const combinedFilter = update(filter.rule, {
      $push: [[drilldownRule]],
    });

    // add to breadcrumb
    const crumbRules = getRulesFromFilter([[drilldownRule]], true);
    const crumbFilter = getFilterFromRules(crumbRules, true);
    yield put(pushDrilldownFilter(crumbFilter.name, crumbFilter.rule));

    // parse and apply filter
    const newRules = getRulesFromFilter(combinedFilter, true);
    const newFilter = getFilterFromRules(newRules, true);
    yield put(applyFilter(newFilter));
  }
}

function* multiDimensionDrilldown(action) {
  const { payload = [] } = action;
  const drilldownRules = [];
  const dimensionList = yield select(allDimensionsListSelector);
  const filter = yield select(getFirstAppliedFilter);
  payload.forEach(({ dimension, row }) => {
    // get current dimesion and filter
    const dimensionObj = dimensionList.find((d) => d.groupBy === dimension);

    // create drill down rule
    const drilldownRule = createFilterRule(
      dimensionObj,
      get(row, 'name[0]', ''),
      get(row, 'value[0]', ''),
    );
    const doesRuleExist = checkIfRuleAlreadyExist(filter.rule, drilldownRule);
    if (!doesRuleExist) {
      drilldownRules.push(drilldownRule);
    }
  });
  if (drilldownRules.length) {
    const combinedFilter = update(filter.rule, {
      $push: [...drilldownRules.map((rule) => [rule])],
    });

    // add to breadcrumb
    const crumbRules = drilldownRules.map((drilldownRule) => {
      const crumbRules = getRulesFromFilter([[drilldownRule]], true);
      return getFilterFromRules(crumbRules, true);
    });
    yield put(pushMultiDrilldownFilter(crumbRules));

    // parse and apply filter
    const newRules = getRulesFromFilter(combinedFilter, true);
    const newFilter = getFilterFromRules(newRules, true);
    yield put(applyFilter(newFilter));
  }
}

function* modifyRealtimeTrends({ params }) {
  const { appliedFilter } = params;
  const rules = getRulesFromFilter(appliedFilter[0].rule, true);
  yield put(actions.setRules(Object.values(rules)));

  yield put(actions.setViewContext(ViewContexts.RT_TRENDS));
}

function* modifyRealtimeFilters({ params }) {
  const { appliedFilter } = params;

  yield put(
    actions.modifyCompareInit({
      filters: appliedFilter,
      activeFilterIndex: 0,
      modifyCompareId: null,
      type: COMPARE_TYPES.dashboard,
    }),
  );

  // select and load rule set
  yield put(actions.setActiveFilter(0));

  yield put(actions.setViewContext(ViewContexts.RT_FILTERS));
}

function* applyErrorFilter(action) {
  const { code, metric } = action;
  const liveMode = yield select(realTimeModeOnTrendsSelector);

  const appliedErrorCodes = yield select(metricFilterErrorCodesSelector);

  if (liveMode) {
    message.warning('Secondary filtering is not supported while on real time mode');
    return;
  }

  // error codes for IN condition
  const metricErrorCodes = get(appliedErrorCodes, [metric], []);
  const errorCodes = new Set(metricErrorCodes);

  // error code works like a toggle, remove if code is already present
  // TODO: swap code workflow
  if (errorCodes.has(code)) {
    errorCodes.delete(code);
  } else {
    errorCodes.add(code);
  }

  if (errorCodes.size > 0) {
    const rule = buildErrorRule(metric, Array.from(errorCodes));

    yield put(
      actions.addMetricFilter({
        metric,
        rule,
        meta: { type: METRIC_FILTER_TYPES.error },
      }),
    );
  } else {
    // remove error filter
    yield put(
      actions.removeMetricFilter({
        metric,
        meta: { type: METRIC_FILTER_TYPES.error },
      }),
    );
  }
}

function* watchInit() {
  yield takeLatest([types.INIT], init);
}

function* watchDimensionDrilldown() {
  yield takeLatest([types.DIMENSION_DRILLDOWN], dimensionDrillown);
}

function* watchMultiDimensionDrilldown() {
  yield takeLatest([types.MULTI_DIMENSION_DRILLDOWN], multiDimensionDrilldown);
}

function* watchModifyRealtimeTrends() {
  yield takeLatest([SET_INITIAL_TRENDS_FILTER_TO_DRAWER], modifyRealtimeTrends);
}

function* watchModifyRealtimeFilters() {
  yield takeLatest([SET_INITIAL_FILTERS_TO_DRAWER], modifyRealtimeFilters);
}

function* watchAddMetricFilter() {
  yield takeLatest([types.INIT_METRIC_FILTER], secondaryMetricUpdates);
}

function* watchApplyErrorFilter() {
  yield takeLatest([types.APPLY_ERROR_FILTER], applyErrorFilter);
}

export default [
  watchInit,
  watchDimensionDrilldown,
  watchMultiDimensionDrilldown,
  watchModifyRealtimeTrends,
  watchModifyRealtimeFilters,
  watchAddMetricFilter,
  watchApplyErrorFilter,
  ...dimensionSaga,
  ...drawerSaga,
];
